import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import {
  CoreProviderFieldDescriptor,
  CoreProviderFieldOption,
} from '@core/api/core';

import {
  defaultProviderFieldValue,
  groupProviderFields,
  isProviderFieldValueActive,
  mergeProviderFieldOptionsWithSelected,
  ProviderFieldValueChange,
  selectedProviderFieldOptionValues,
} from './provider-filter-fields.utils';

@Component({
  selector: 'app-provider-filter-fields',
  templateUrl: './provider-filter-fields.component.html',
  styleUrl: './provider-filter-fields.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProviderFilterFieldsComponent {
  readonly fields = input<CoreProviderFieldDescriptor[]>([]);
  readonly values = input<Record<string, unknown>>({});
  readonly optionMap = input<Record<string, CoreProviderFieldOption[]>>({});
  readonly optionLoadingMap = input<Record<string, boolean>>({});
  readonly optionErrorMap = input<Record<string, boolean>>({});
  readonly disabled = input(false);

  readonly valueChanged = output<ProviderFieldValueChange>();
  readonly requestFieldOptions = output<CoreProviderFieldDescriptor>();
  readonly retryFieldOptions = output<CoreProviderFieldDescriptor>();

  protected readonly fieldGroups = computed(() => groupProviderFields(this.fields()));

  protected isCoreFilterGroup(groupKey: string): boolean {
    return groupKey === 'core_filters';
  }

  protected getOptions(field: CoreProviderFieldDescriptor): CoreProviderFieldOption[] {
    const base =
      field.valuesSource === 'static'
        ? (field.staticValues ?? [])
        : (this.optionMap()[field.name] ?? []);

    return mergeProviderFieldOptionsWithSelected(
      base,
      selectedProviderFieldOptionValues(field, this.getFieldValue(field)),
    );
  }

  protected getFieldValue(field: CoreProviderFieldDescriptor): unknown {
    return this.values()[field.name] ?? defaultProviderFieldValue(field);
  }

  protected getInputValue(field: CoreProviderFieldDescriptor): string | number {
    const value = this.getFieldValue(field);
    return typeof value === 'number' || typeof value === 'string' ? value : '';
  }

  protected booleanSelectValue(field: CoreProviderFieldDescriptor): string {
    const value = this.getFieldValue(field);
    if (value === true) {
      return 'true';
    }
    if (value === false) {
      return 'false';
    }
    return '';
  }

  protected fieldHasValue(field: CoreProviderFieldDescriptor): boolean {
    return isProviderFieldValueActive(field, this.getFieldValue(field));
  }

  protected onInput(field: CoreProviderFieldDescriptor, event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = field.type === 'number' && inputElement.value !== ''
      ? Number(inputElement.value)
      : inputElement.value;
    this.emitValue(field, value);
  }

  protected onSelectChange(field: CoreProviderFieldDescriptor, event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.emitValue(field, selectElement.value);
  }

  protected onBooleanChange(field: CoreProviderFieldDescriptor, event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    if (selectElement.value === '') {
      this.emitValue(field, null);
      return;
    }
    this.emitValue(field, selectElement.value === 'true');
  }

  protected isMultiOptionSelected(field: CoreProviderFieldDescriptor, optionValue: string): boolean {
    return selectedProviderFieldOptionValues(field, this.getFieldValue(field)).includes(
      String(optionValue),
    );
  }

  protected toggleMultiOption(field: CoreProviderFieldDescriptor, optionValue: string): void {
    if (this.disabled()) {
      return;
    }

    const selected = selectedProviderFieldOptionValues(field, this.getFieldValue(field));
    const optionKey = String(optionValue);
    const next = selected.includes(optionKey)
      ? selected.filter(value => value !== optionKey)
      : [...selected, optionKey];
    this.emitValue(field, next);
  }

  protected emitValue(field: CoreProviderFieldDescriptor, value: unknown): void {
    this.valueChanged.emit({ field, value });
  }

  protected clearField(field: CoreProviderFieldDescriptor): void {
    this.emitValue(field, defaultProviderFieldValue(field));
  }

  protected loadOptionsOnFocus(field: CoreProviderFieldDescriptor): void {
    if (field.valuesSource === 'dynamic') {
      this.requestFieldOptions.emit(field);
    }
  }

  protected fieldId(fieldName: string): string {
    return `provider-filter-${fieldName}`;
  }

  protected helpId(fieldName: string): string {
    return `provider-filter-${fieldName}-help`;
  }
}
